'use client';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/ui-kit/utils';
import { formatCents } from '@/lib/utils/formatAmount';
import { BUTTON_M_CLASS } from '../constants';
import type { PriceAlertSide } from '../types';

interface SideToggleProps {
  side: PriceAlertSide;
  onChange: (side: PriceAlertSide) => void;
  yesCents: number;
  noCents: number;
  fullWidth?: boolean;
}

export function SideToggle({
  side,
  onChange,
  yesCents,
  noCents,
  fullWidth = false,
}: SideToggleProps) {
  const t = useTranslations('priceAlerts');

  return (
    <RadioGroupPrimitive.Root
      value={side}
      onValueChange={(v) => onChange(v as PriceAlertSide)}
      className={cn(
        'flex h-10 overflow-hidden rounded-[10px] bg-v2-btn-secondary dark:bg-v2-surface-tertiary',
        fullWidth ? 'w-full' : 'shrink-0',
      )}
      aria-label="Side"
    >
      <SideOption
        value="YES"
        active={side === 'YES'}
        fullWidth={fullWidth}
        activeClass="bg-v2-toggle-green-surface text-v2-green"
      >
        {t('side.yes')} {formatCents(yesCents)}
      </SideOption>
      <SideOption
        value="NO"
        active={side === 'NO'}
        fullWidth={fullWidth}
        activeClass="bg-v2-toggle-red-surface text-red-400"
      >
        {t('side.no')} {formatCents(noCents)}
      </SideOption>
    </RadioGroupPrimitive.Root>
  );
}

interface SideOptionProps {
  value: PriceAlertSide;
  active: boolean;
  fullWidth: boolean;
  activeClass: string;
  children: React.ReactNode;
}

function SideOption({ value, active, fullWidth, activeClass, children }: SideOptionProps) {
  return (
    <RadioGroupPrimitive.Item
      value={value}
      className={cn(
        'flex h-full cursor-pointer items-center justify-center transition-colors outline-none focus-visible:ring-1 focus-visible:ring-v2-border',
        fullWidth ? 'flex-1' : 'px-5',
        BUTTON_M_CLASS,
        active ? activeClass : 'bg-transparent text-v2-text-secondary hover:text-v2-text-primary',
      )}
    >
      {children}
    </RadioGroupPrimitive.Item>
  );
}
